import { FunctionComponent, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";
import { Organization } from "fhir/r5";
import HierarchyChartView from "../HierarchyChartView/HierarchyChartView";
import {
  buildOrganizationTree,
  getDisplayName,
  getOrganizationKey,
  getSearchTerms,
} from "../../utils/organizationTree";

type OrganizationChartViewProps = {
  organizations: Organization[];
  loading?: boolean;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
};

const OrganizationChartView: FunctionComponent<OrganizationChartViewProps> = ({
  organizations,
  loading = false,
  searchTerm,
  onSearchTermChange,
}) => {
  const navigate = useNavigate();

  const goToOrganizationDetails = useCallback(
    (organizationId?: string | null) => {
      if (organizationId) {
        navigate(`/Organization/${encodeURIComponent(organizationId)}`);
      }
    },
    [navigate]
  );

  return (
    <HierarchyChartView<Organization>
      items={organizations}
      loading={loading}
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      buildTree={buildOrganizationTree}
      getKey={getOrganizationKey}
      getDisplayName={getDisplayName}
      getSearchTerms={getSearchTerms}
      onGoToDetails={goToOrganizationDetails}
      labels={{
        searchPlaceholder: i18n.t("label.organizationsearch"),
        reset: i18n.t("button.reset"),
        center: i18n.t("button.centerview"),
        expandAll: i18n.t("button.expandall"),
        collapseAll: i18n.t("button.collapseall"),
        root: i18n.t("label.root"),
        level: i18n.t("label.level"),
        childItems: i18n.t("label.suborganizations"),
        leaf: i18n.t("label.leaf"),
        searchResults: i18n.t("label.searchresults"),
        structureEmpty: i18n.t("title.organizationstructureempty"),
        structureNoResults: i18n.t("title.organizationstructurenoresults"),
        open: i18n.t("label.openorganization"),
        close: i18n.t("label.closeorganization"),
        details: i18n.t("label.details"),
        totalItemsLabel: i18n.t("label.organizations"),
        rootItemsLabel: i18n.t("label.rootorganizations"),
        leafItemsLabel: i18n.t("label.leaforganizations"),
      }}
    />
  );
};

export default OrganizationChartView;
